/* eslint-disable import/first */
import {
  createStore, applyMiddleware, compose, Middleware,
} from 'redux';
import Thunk from 'redux-thunk';
import { createLogger } from 'redux-logger';
import { configure, ErrorStatus } from 'redux-and-the-rest';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import reducers from './reducers';
import deserializeResponse from './reducers/utils/deserializeResponse';
import serializeRequest from './reducers/utils/serializeRequest';
import setAuthConfig from './middleware/setAuthConfig';

const middleWare: Middleware[] = [
  // Middleware to handle asynchronous actions in Redux
  Thunk,
  setAuthConfig,
];

if (process.env.NODE_ENV === 'development') {
  // Redux-logger to help see the current state of the store in a debugging tool
  middleWare.push(createLogger());
}

const persistConfig = {
  key: 'root',
  whitelist: ['authentication'],
  storage,
};

const persistedReducer = persistReducer(persistConfig, reducers);

const store = createStore(persistedReducer, {}, compose(
  applyMiddleware(...middleWare),
));

const persistor = persistStore(store);

configure({
  store,
  responseAdaptor: deserializeResponse,
  requestAdaptor: serializeRequest,
  acceptType: 'application/vnd.api+json',
  contentType: 'application/vnd.api+json',
  urlOnlyParams: ['include'],
  request: {
    errorHandler: async (response: Response, dispatchHandler: (errorOrErrors: ErrorStatus) => void) => {
      // Get the text of the reponse to handle errors which don't contain a valid body.
      const text = await response.text();

      if (text === '') {
        /**
         * 401 responses don't contain a body and can't be parsed without an error.
         * In this situation we just create a basic error message and pass that back to the redux-and-the-rest handler.
         */
        dispatchHandler({
          type: response.type,
        });
        return;
      }

      const { errors = [] } = JSON.parse(text);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const newErrors = errors.map((error: any) => {
        const errorStatus: ErrorStatus = {
          type: error.code,
          originalError: error,
        };

        return errorStatus;
      });
      dispatchHandler(newErrors);
    },
  },
});

export { store, persistor };
